import { useState, useEffect } from 'react';
import {
  Save,
  X,
  UserPlus,
  DollarSign,
  Mail,
  Phone,
  Calendar,
  Pause,
  Play,
  Ban,
  Eye,
  ChevronLeft,
  Edit2,
  Search,
  CreditCard,
  ExternalLink,
  RefreshCw,
  Copy,
  RotateCcw,
  AlertCircle,
  FileText,
  Loader2
} from 'lucide-react';

import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { adminFetch, getAuthHeaders } from '../../services/adminApi';

import { PageTitle } from './shared';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// ============================================
// Subscription Settings Editor
// ============================================
const SubscriptionSettingsEditor = () => {
  const [settings, setSettings] = useState({
    default_monthly_rate: 120.00,
    quickstart_fee: 45.00
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await adminFetch(`${API_BASE}/api/admin/subscription-settings`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const response = await adminFetch(`${API_BASE}/api/admin/subscription-settings`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          default_monthly_rate: parseFloat(settings.default_monthly_rate),
          quickstart_fee: parseFloat(settings.quickstart_fee)
        })
      });
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      }
    } catch (error) {
      console.error('Failed to save settings:', error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-center py-4">Loading settings...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="monthly_rate">Default Monthly Rate ($)</Label>
          <Input
            id="monthly_rate"
            type="number"
            step="0.01"
            value={settings.default_monthly_rate}
            onChange={(e) => setSettings({ ...settings, default_monthly_rate: e.target.value })}
            className="mt-1"
          />
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Default rate for new customers</p>
        </div>
        <div>
          <Label htmlFor="quickstart_fee">QuickStart Fee ($)</Label>
          <Input
            id="quickstart_fee"
            type="number"
            step="0.01"
            value={settings.quickstart_fee}
            onChange={(e) => setSettings({ ...settings, quickstart_fee: e.target.value })}
            className="mt-1"
          />
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">One-time fee for 2-week trial</p>
        </div>
      </div>
      <Button onClick={saveSettings} disabled={saving}>
        <Save className="h-4 w-4 mr-2" />
        {saving ? 'Saving...' : 'Save Settings'}
      </Button>
    </div>
  );
};

// ============================================
// Customer Editor Dialog
// ============================================
const CustomerEditorDialog = ({ open, onClose, customer, onSave }) => {
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    phone: '',
    account_type: 'parent',
    monthly_rate: 120.00,
    quickstart_enabled: false,
    subscription_status: 'none',
    notes: ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const isEditing = !!customer;

  useEffect(() => {
    if (customer) {
      setFormData({
        email: customer.email || '',
        name: customer.name || '',
        phone: customer.phone || '',
        account_type: customer.account_type || 'parent',
        monthly_rate: customer.monthly_rate || 120.00,
        quickstart_enabled: customer.quickstart_enabled || false,
        subscription_status: customer.subscription_status || 'none',
        notes: customer.notes || ''
      });
    } else {
      setFormData({
        email: '',
        name: '',
        phone: '',
        account_type: 'parent',
        monthly_rate: 120.00,
        quickstart_enabled: false,
        subscription_status: 'none',
        notes: ''
      });
    }
    setError('');
  }, [customer, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      const url = isEditing
        ? `${API_BASE}/api/admin/customers/${customer.id}`
        : `${API_BASE}/api/admin/customers`;

      const response = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          ...formData,
          monthly_rate: parseFloat(formData.monthly_rate)
        })
      });

      if (response.ok) {
        onSave();
        onClose();
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to save customer');
      }
    } catch {
      setError('Failed to save customer');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Customer' : 'Add New Customer'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 px-3 py-2 rounded text-sm">
              {error}
            </div>
          )}
          <div>
            <Label htmlFor="email">Email *</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              disabled={isEditing}
              required
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="name">Name *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="phone">Phone</Label>
            <Input
              id="phone"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="account_type">Account Type</Label>
            <select
              id="account_type"
              value={formData.account_type}
              onChange={(e) => setFormData({ ...formData, account_type: e.target.value })}
              className="w-full mt-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="individual">Individual (Adult Student)</option>
              <option value="parent">Parent/Guardian</option>
            </select>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Individual: Customer is the student. Parent: Customer has child student(s).
            </p>
          </div>
          <div>
            <Label htmlFor="rate">Monthly Rate ($)</Label>
            <Input
              id="rate"
              type="number"
              step="0.01"
              value={formData.monthly_rate}
              onChange={(e) => setFormData({ ...formData, monthly_rate: e.target.value })}
              className="mt-1"
            />
          </div>
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="quickstart_enabled"
              checked={formData.quickstart_enabled}
              onChange={(e) => setFormData({ ...formData, quickstart_enabled: e.target.checked })}
              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <Label htmlFor="quickstart_enabled" className="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
              Enable QuickStart for this customer
            </Label>
          </div>
          {isEditing && (
            <div>
              <Label htmlFor="subscription_status">Subscription Status</Label>
              <select
                id="subscription_status"
                value={formData.subscription_status}
                onChange={(e) => setFormData({ ...formData, subscription_status: e.target.value })}
                className="w-full mt-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="none">New (No Subscription)</option>
                <option value="active">Active</option>
                <option value="paused">Paused</option>
                <option value="cancelled">Cancelled</option>
                <option value="past_due">Past Due</option>
              </select>
            </div>
          )}
          <div>
            <Label htmlFor="notes">Notes</Label>
            <Input
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="e.g., Family discount, 2 children enrolled"
              className="mt-1"
            />
          </div>
          <div className="flex justify-end space-x-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : isEditing ? 'Update' : 'Add Customer'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ============================================
// Change Price Dialog
// ============================================
const ChangePriceDialog = ({ open, onClose, currentAmount, onSubmit, loading }) => {
  const [newAmount, setNewAmount] = useState('');

  useEffect(() => {
    if (open && currentAmount) {
      setNewAmount(currentAmount.toString());
    }
  }, [open, currentAmount]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const amount = parseFloat(newAmount);
    if (isNaN(amount) || amount <= 0) {
      alert('Please enter a valid amount');
      return;
    }
    onSubmit({ new_amount: amount });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Change Subscription Price</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="newAmount">New Monthly Amount ($)</Label>
            <Input
              id="newAmount"
              type="number"
              step="0.01"
              min="0"
              value={newAmount}
              onChange={(e) => setNewAmount(e.target.value)}
              className="mt-1"
              required
            />
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Current: ${currentAmount}/mo. Prorated charges will apply.
            </p>
          </div>
          <div className="flex justify-end space-x-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Updating...
                </>
              ) : (
                'Update Price'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ============================================
// Refund Dialog
// ============================================
const RefundDialog = ({ open, onClose, invoice, onSubmit, loading }) => {
  const [refundAmount, setRefundAmount] = useState('');
  const [reason, setReason] = useState('requested_by_customer');
  const [isFullRefund, setIsFullRefund] = useState(true);

  useEffect(() => {
    if (open && invoice) {
      setRefundAmount(invoice.amount_paid.toString());
      setIsFullRefund(true);
    }
  }, [open, invoice]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const amount = parseFloat(refundAmount);
    if (!isFullRefund && (isNaN(amount) || amount <= 0 || amount > invoice.amount_paid)) {
      alert('Please enter a valid refund amount');
      return;
    }

    onSubmit({
      payment_intent_id: invoice.payment_intent,
      amount: isFullRefund ? null : amount,
      reason
    });
  };

  if (!invoice) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Refund Payment</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded">
            <p className="text-sm">
              Invoice: <span className="font-medium">{invoice.number || invoice.id}</span>
            </p>
            <p className="text-sm">
              Amount Paid: <span className="font-medium">${invoice.amount_paid}</span>
            </p>
          </div>

          <div className="space-y-2">
            <Label>Refund Type</Label>
            <div className="flex gap-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  checked={isFullRefund}
                  onChange={() => setIsFullRefund(true)}
                  className="mr-2"
                />
                Full Refund (${invoice.amount_paid})
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  checked={!isFullRefund}
                  onChange={() => setIsFullRefund(false)}
                  className="mr-2"
                />
                Partial Refund
              </label>
            </div>
          </div>

          {!isFullRefund && (
            <div>
              <Label htmlFor="refundAmount">Refund Amount ($)</Label>
              <Input
                id="refundAmount"
                type="number"
                step="0.01"
                min="0.01"
                max={invoice.amount_paid}
                value={refundAmount}
                onChange={(e) => setRefundAmount(e.target.value)}
                className="mt-1"
                required
              />
            </div>
          )}

          <div>
            <Label htmlFor="reason">Reason</Label>
            <select
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full mt-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-background text-foreground"
            >
              <option value="requested_by_customer">Requested by Customer</option>
              <option value="duplicate">Duplicate Payment</option>
              <option value="fraudulent">Fraudulent</option>
            </select>
          </div>

          <div className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 p-3 rounded text-sm text-yellow-800 dark:text-yellow-300">
            <strong>Warning:</strong> Refunds are permanent and cannot be undone.
          </div>

          <div className="flex justify-end space-x-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" className="bg-gradient-to-r from-red-800 to-red-900 text-amber-50 border border-amber-600/30 hover:from-red-700 hover:to-red-800" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Process Refund
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ============================================
// Customer List
// ============================================
const CustomerList = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerDetail, setCustomerDetail] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Stripe subscription state
  const [stripeData, setStripeData] = useState(null);
  const [stripeInvoices, setStripeInvoices] = useState([]);
  const [stripeLoading, setStripeLoading] = useState(false);
  const [stripeError, setStripeError] = useState(null);
  const [showChangePriceDialog, setShowChangePriceDialog] = useState(false);
  const [showRefundDialog, setShowRefundDialog] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Invite state — keyed by customer id so it resets when switching customers
  const [inviteState, setInviteState] = useState({ status: 'idle', message: '' });

  useEffect(() => {
    fetchCustomers();
  }, []);

  // Reset invite state whenever we open a different customer
  useEffect(() => {
    setInviteState({ status: 'idle', message: '' });
  }, [selectedCustomer]);

  const fetchCustomers = async () => {
    try {
      const response = await adminFetch(`${API_BASE}/api/admin/customers`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setCustomers(data);
      }
    } catch (error) {
      console.error('Failed to fetch customers:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomerDetail = async (customerId) => {
    try {
      const response = await adminFetch(`${API_BASE}/api/admin/customers/${customerId}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setCustomerDetail(data);
        setSelectedCustomer(customerId);
        // Fetch Stripe data if customer has Stripe subscription
        if (data.customer.payment_method === 'stripe' || data.customer.stripe_subscription_id) {
          fetchStripeData(customerId);
        } else {
          setStripeData(null);
          setStripeInvoices([]);
        }
      }
    } catch (error) {
      console.error('Failed to fetch customer detail:', error);
    }
  };

  const fetchStripeData = async (customerId) => {
    setStripeLoading(true);
    setStripeError(null);
    try {
      // Fetch subscription details and invoices in parallel
      const [subResponse, invResponse] = await Promise.all([
        adminFetch(`${API_BASE}/api/admin/stripe/subscription/${customerId}`, {
          headers: getAuthHeaders()
        }),
        adminFetch(`${API_BASE}/api/admin/stripe/invoices/${customerId}`, {
          headers: getAuthHeaders()
        })
      ]);

      if (subResponse.ok) {
        const subData = await subResponse.json();
        setStripeData(subData);
      }

      if (invResponse.ok) {
        const invData = await invResponse.json();
        setStripeInvoices(invData.invoices || []);
      }
    } catch (error) {
      console.error('Failed to fetch Stripe data:', error);
      setStripeError('Failed to load Stripe data');
    } finally {
      setStripeLoading(false);
    }
  };

  const handleStripeAction = async (action, customerId, extraData = {}) => {
    setActionLoading(true);
    try {
      let url, method = 'POST', body = null;

      switch (action) {
        case 'pause':
          url = `${API_BASE}/api/admin/stripe/subscription/${customerId}/pause`;
          break;
        case 'resume':
          url = `${API_BASE}/api/admin/stripe/subscription/${customerId}/resume`;
          break;
        case 'cancel':
          url = `${API_BASE}/api/admin/customers/${customerId}/cancel`;
          break;
        case 'cancel-now':
          url = `${API_BASE}/api/admin/stripe/subscription/${customerId}/cancel-now`;
          break;
        case 'change-price':
          url = `${API_BASE}/api/admin/stripe/subscription/${customerId}/price`;
          method = 'PUT';
          body = JSON.stringify(extraData);
          break;
        case 'refund':
          url = `${API_BASE}/api/admin/stripe/refund`;
          body = JSON.stringify(extraData);
          break;
        case 'sync-payments':
          url = `${API_BASE}/api/admin/stripe/customers/${customerId}/sync-payments`;
          break;
        case 'retry-invoice':
          url = `${API_BASE}/api/admin/stripe/invoices/${extraData.invoiceId}/pay`;
          break;
        default:
          throw new Error('Unknown action');
      }

      const response = await adminFetch(url, {
        method,
        headers: getAuthHeaders(),
        body
      });

      if (response.ok) {
        // Refresh data
        await fetchCustomers();
        if (selectedCustomer) {
          await fetchCustomerDetail(selectedCustomer);
        }
        return true;
      } else {
        const data = await response.json();
        alert(data.detail || 'Action failed');
        return false;
      }
    } catch (error) {
      console.error(`Failed to ${action}:`, error);
      alert(`Failed to ${action}`);
      return false;
    } finally {
      setActionLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    // Could add a toast notification here
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp * 1000).toLocaleDateString();
  };

  const handleSubscriptionAction = async (customerId, action) => {
    const confirmMsg = {
      cancel: 'Are you sure you want to cancel this subscription?',
      pause: 'Are you sure you want to pause this subscription?',
      resume: 'Resume this subscription?'
    };

    if (!window.confirm(confirmMsg[action])) return;

    try {
      const response = await adminFetch(`${API_BASE}/api/admin/customers/${customerId}/${action}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        await fetchCustomers();
        if (selectedCustomer === customerId) {
          await fetchCustomerDetail(customerId);
        }
      }
    } catch (error) {
      console.error(`Failed to ${action} subscription:`, error);
    }
  };

  const handleSendInvite = async (customerId, { force = false } = {}) => {
    const activeInvite = customerDetail?.latest_invite;
    if (activeInvite && !force) {
      const daysUntilExpiry = Math.max(
        0,
        Math.ceil((new Date(activeInvite.expires_at) - Date.now()) / (1000 * 60 * 60 * 24))
      );
      const confirmed = window.confirm(
        `An invite is already active for this customer (expires in ${daysUntilExpiry} day${daysUntilExpiry === 1 ? '' : 's'}). Send a new one anyway?`
      );
      if (!confirmed) return;
    }

    setInviteState({ status: 'sending', message: '' });
    try {
      const response = await adminFetch(
        `${API_BASE}/api/admin/customers/${customerId}/invite`,
        { method: 'POST', headers: getAuthHeaders() }
      );
      if (response.ok) {
        const data = await response.json();
        setInviteState({
          status: 'success',
          message: `Invite sent to ${customerDetail?.customer?.email || 'customer'}. Link expires in ${data.expires_days} days.`,
        });
        await fetchCustomerDetail(customerId);
      } else {
        const data = await response.json().catch(() => ({}));
        setInviteState({
          status: 'error',
          message: data.detail || `Failed to send invite (HTTP ${response.status}).`,
        });
      }
    } catch {
      setInviteState({
        status: 'error',
        message: 'Network error — could not send invite. Please retry.',
      });
    }
  };

  const handleContentAccessChange = async (override) => {
    try {
      const response = await adminFetch(
        `${API_BASE}/api/admin/customers/${selectedCustomer}/content-access`,
        {
          method: 'PUT',
          headers: getAuthHeaders(),
          body: JSON.stringify({ override })
        }
      );
      if (response.ok) {
        await fetchCustomerDetail(selectedCustomer);
      }
    } catch (error) {
      console.error('Failed to update content access:', error);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      none: 'bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300',
      active: 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300',
      past_due: 'bg-orange-100 dark:bg-orange-900/50 text-orange-800 dark:text-orange-300',
      cancelled: 'bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-300',
      paused: 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-300'
    };
    const labels = {
      none: 'New',
      active: 'Active',
      past_due: 'Past Due',
      cancelled: 'Cancelled',
      paused: 'Paused'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 dark:bg-gray-800'}`}>
        {labels[status] || status}
      </span>
    );
  };

  const filteredCustomers = customers.filter(customer => {
    const matchesSearch = !searchTerm ||
      customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.email.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'all' ||
      customer.subscription_status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const handleAddCustomer = () => {
    setEditingCustomer(null);
    setShowEditor(true);
  };

  const handleEditCustomer = (customer, e) => {
    e.stopPropagation();
    setEditingCustomer(customer);
    setShowEditor(true);
  };

  if (loading) {
    return <div className="text-center py-4">Loading...</div>;
  }

  // Customer Detail View
  if (selectedCustomer && customerDetail) {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <Button variant="outline" onClick={() => { setSelectedCustomer(null); setCustomerDetail(null); }}>
            <ChevronLeft className="h-4 w-4 mr-2" />
            Back to Customers
          </Button>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => handleSendInvite(selectedCustomer)}
              disabled={
                inviteState.status === 'sending' ||
                customerDetail.customer.subscription_status === 'cancelled'
              }
              title={
                customerDetail.customer.subscription_status === 'cancelled'
                  ? 'Cannot invite a cancelled customer'
                  : 'Send member portal setup invite'
              }
            >
              {inviteState.status === 'sending' ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Mail className="h-4 w-4 mr-2" />
              )}
              Invite
            </Button>
            <Button variant="outline" onClick={() => handleEditCustomer(customerDetail.customer, { stopPropagation: () => {} })}>
              <Edit2 className="h-4 w-4 mr-2" />
              Edit
            </Button>
          </div>
        </div>

        {/* Invite status banner */}
        {(inviteState.status === 'success' || inviteState.status === 'error' || customerDetail.latest_invite) && (
          <div
            className={`p-3 rounded-md border text-sm flex items-start gap-2 ${
              inviteState.status === 'success'
                ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-800 dark:text-green-200'
                : inviteState.status === 'error'
                ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-800 dark:text-red-200'
                : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-200'
            }`}
          >
            <Mail className="h-4 w-4 mt-0.5 shrink-0" />
            <div>
              {inviteState.status === 'success' || inviteState.status === 'error' ? (
                inviteState.message
              ) : (
                <>
                  Invite is active — sent {new Date(customerDetail.latest_invite.created_at).toLocaleDateString()}, expires{' '}
                  {new Date(customerDetail.latest_invite.expires_at).toLocaleDateString()}
                  {' '}({Math.max(0, Math.ceil((new Date(customerDetail.latest_invite.expires_at) - Date.now()) / (1000 * 60 * 60 * 24)))} day(s) left).
                </>
              )}
            </div>
          </div>
        )}

        <Card>
          <CardHeader>
            <div className="flex justify-between items-start">
              <div>
                <CardTitle>{customerDetail.customer.name}</CardTitle>
                <p className="text-sm text-gray-500 dark:text-gray-400">{customerDetail.customer.email}</p>
                {customerDetail.customer.phone && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center mt-1">
                    <Phone className="h-3 w-3 mr-1" />
                    {customerDetail.customer.phone}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span className={`px-2 py-1 rounded text-xs ${
                  customerDetail.customer.account_type === 'individual'
                    ? 'bg-purple-100 dark:bg-purple-900/50 text-purple-800 dark:text-purple-300'
                    : 'bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300'
                }`}>
                  {customerDetail.customer.account_type === 'individual' ? 'Individual' : 'Parent'}
                </span>
                {getStatusBadge(customerDetail.customer.subscription_status)}
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <Label className="text-gray-500 dark:text-gray-400">Monthly Rate</Label>
                <p className="font-medium">${customerDetail.customer.monthly_rate}</p>
              </div>
              {customerDetail.customer.quickstart_enabled && (
                <div>
                  <Label className="text-gray-500 dark:text-gray-400">QuickStart</Label>
                  <p className="font-medium">
                    {customerDetail.customer.quickstart_paid ? 'Paid' : 'Not Paid'}
                  </p>
                </div>
              )}
              <div>
                <Label className="text-gray-500 dark:text-gray-400">Next Billing</Label>
                <p className="font-medium">
                  {customerDetail.customer.next_billing_date
                    ? new Date(customerDetail.customer.next_billing_date).toLocaleDateString()
                    : 'N/A'}
                </p>
              </div>
              <div>
                <Label className="text-gray-500 dark:text-gray-400">Payment Method</Label>
                <p className="font-medium">
                  {customerDetail.customer.payment_method || 'None'}
                </p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-wrap gap-2 pt-4 border-t">
              {customerDetail.customer.subscription_status === 'active' && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleSubscriptionAction(selectedCustomer, 'pause')}
                  >
                    <Pause className="h-4 w-4 mr-2" />
                    Pause
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleSubscriptionAction(selectedCustomer, 'cancel')}
                  >
                    <Ban className="h-4 w-4 mr-2" />
                    Cancel
                  </Button>
                </>
              )}
              {customerDetail.customer.subscription_status === 'paused' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleSubscriptionAction(selectedCustomer, 'resume')}
                >
                  <Play className="h-4 w-4 mr-2" />
                  Resume
                </Button>
              )}
            </div>

            {/* Content Access Control */}
            <div className="pt-4 border-t">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium">Content Access</h4>
                <span className={`px-2 py-1 rounded text-xs font-medium ${
                  customerDetail.customer.content_access_restricted
                    ? 'bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-300'
                    : 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300'
                }`}>
                  Resources & Learning: {customerDetail.customer.content_access_restricted ? 'Hidden' : 'Visible'}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <select
                  value={customerDetail.customer.content_access_override || 'auto'}
                  onChange={(e) => handleContentAccessChange(e.target.value === 'auto' ? null : e.target.value)}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-background text-foreground"
                >
                  <option value="auto">Automatic (based on payment status)</option>
                  <option value="show">Always Show</option>
                  <option value="hide">Always Hide</option>
                </select>
              </div>
              {customerDetail.customer.content_access_auto_reason && !customerDetail.customer.content_access_override && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  Auto-restricted: {customerDetail.customer.content_access_auto_reason === 'payment_failed' ? 'Payment failed' : customerDetail.customer.content_access_auto_reason}
                </p>
              )}
            </div>

            {/* Stripe Subscription Section */}
            {(customerDetail.customer.payment_method === 'stripe' || customerDetail.customer.stripe_subscription_id) && (
              <div className="pt-4 border-t">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium flex items-center">
                    <CreditCard className="h-4 w-4 mr-2 text-blue-600" />
                    Stripe Subscription
                  </h4>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fetchStripeData(selectedCustomer)}
                      disabled={stripeLoading}
                    >
                      <RefreshCw className={`h-3 w-3 mr-1 ${stripeLoading ? 'animate-spin' : ''}`} />
                      Refresh
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={async () => {
                        const result = await handleStripeAction('sync-payments', selectedCustomer);
                        if (result) {
                          await fetchCustomerDetail(selectedCustomer);
                          await fetchStripeData(selectedCustomer);
                        }
                      }}
                      disabled={actionLoading || stripeLoading}
                    >
                      Sync Payments
                    </Button>
                    {stripeData?.stripe_customer_id && (
                      <a
                        href={`https://dashboard.stripe.com/customers/${stripeData.stripe_customer_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:underline flex items-center"
                      >
                        View in Stripe <ExternalLink className="h-3 w-3 ml-1" />
                      </a>
                    )}
                  </div>
                </div>

                {stripeLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                  </div>
                ) : stripeError ? (
                  <div className="bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 p-3 rounded flex items-center">
                    <AlertCircle className="h-4 w-4 mr-2" />
                    {stripeError}
                  </div>
                ) : stripeData?.has_subscription && stripeData.subscription ? (
                  <div className="space-y-4">
                    {/* Subscription Details */}
                    <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 space-y-3">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-500 dark:text-gray-400">Customer ID:</span>
                          <div className="flex items-center gap-1">
                            <code className="text-xs bg-gray-100 dark:bg-gray-700 px-1 rounded">
                              {stripeData.stripe_customer_id?.slice(0, 15)}...
                            </code>
                            <button
                              onClick={() => copyToClipboard(stripeData.stripe_customer_id)}
                              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                            >
                              <Copy className="h-3 w-3" />
                            </button>
                          </div>
                        </div>
                        <div>
                          <span className="text-gray-500 dark:text-gray-400">Subscription ID:</span>
                          <div className="flex items-center gap-1">
                            <code className="text-xs bg-gray-100 dark:bg-gray-700 px-1 rounded">
                              {stripeData.subscription.id?.slice(0, 15)}...
                            </code>
                            <button
                              onClick={() => copyToClipboard(stripeData.subscription.id)}
                              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                            >
                              <Copy className="h-3 w-3" />
                            </button>
                          </div>
                        </div>
                        <div>
                          <span className="text-gray-500 dark:text-gray-400">Status:</span>
                          <span className={`ml-2 px-2 py-0.5 rounded text-xs font-medium ${
                            stripeData.subscription.status === 'active' ? 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300' :
                            stripeData.subscription.status === 'past_due' ? 'bg-orange-100 dark:bg-orange-900/50 text-orange-800 dark:text-orange-300' :
                            stripeData.subscription.status === 'canceled' ? 'bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-300' :
                            'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-300'
                          }`}>
                            {stripeData.subscription.status}
                            {stripeData.subscription.cancel_at_period_end && ' (canceling)'}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-500 dark:text-gray-400">Amount:</span>
                          <span className="ml-2 font-medium">
                            ${stripeData.subscription.price?.amount || customerDetail.customer.monthly_rate}/mo
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-500 dark:text-gray-400">Current Period:</span>
                          <span className="ml-2">
                            {formatTimestamp(stripeData.subscription.current_period_start)} →{' '}
                            {formatTimestamp(stripeData.subscription.current_period_end)}
                          </span>
                        </div>
                        {stripeData.subscription.payment_method && (
                          <div>
                            <span className="text-gray-500 dark:text-gray-400">Payment Method:</span>
                            <span className="ml-2">
                              {stripeData.subscription.payment_method.type === 'card' ? (
                                <>
                                  {stripeData.subscription.payment_method.brand} ••••{stripeData.subscription.payment_method.last4}
                                </>
                              ) : (
                                <>
                                  {stripeData.subscription.payment_method.bank_name} ••••{stripeData.subscription.payment_method.last4}
                                </>
                              )}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Subscription Actions */}
                      <div className="flex flex-wrap gap-2 pt-3 border-t border-gray-200 dark:border-gray-700">
                        {stripeData.subscription.status === 'active' && !stripeData.subscription.pause_collection && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                if (window.confirm('Pause this subscription? Billing will be suspended.')) {
                                  handleStripeAction('pause', selectedCustomer);
                                }
                              }}
                              disabled={actionLoading}
                            >
                              <Pause className="h-3 w-3 mr-1" />
                              Pause
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                if (window.confirm('Cancel at end of billing period?')) {
                                  handleStripeAction('cancel', selectedCustomer);
                                }
                              }}
                              disabled={actionLoading}
                            >
                              <Ban className="h-3 w-3 mr-1" />
                              Cancel at Period End
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-red-600 dark:text-red-400 border-red-300 dark:border-red-700 hover:bg-red-50 dark:hover:bg-red-900/30"
                              onClick={() => {
                                if (window.confirm('Cancel IMMEDIATELY? Customer will lose access right away.')) {
                                  handleStripeAction('cancel-now', selectedCustomer);
                                }
                              }}
                              disabled={actionLoading}
                            >
                              <X className="h-3 w-3 mr-1" />
                              Cancel Now
                            </Button>
                          </>
                        )}
                        {stripeData.subscription.pause_collection && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              if (window.confirm('Resume this subscription?')) {
                                handleStripeAction('resume', selectedCustomer);
                              }
                            }}
                            disabled={actionLoading}
                          >
                            <Play className="h-3 w-3 mr-1" />
                            Resume
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowChangePriceDialog(true)}
                          disabled={actionLoading || stripeData.subscription.status === 'canceled'}
                        >
                          <DollarSign className="h-3 w-3 mr-1" />
                          Change Price
                        </Button>
                      </div>
                    </div>

                    {/* Invoices from Stripe */}
                    {stripeInvoices.length > 0 && (
                      <div>
                        <h5 className="text-sm font-medium mb-2 flex items-center">
                          <FileText className="h-4 w-4 mr-1" />
                          Recent Invoices
                        </h5>
                        <div className="space-y-2">
                          {stripeInvoices.slice(0, 5).map((invoice) => (
                            <div key={invoice.id} className="flex justify-between items-center p-2 bg-gray-50 dark:bg-gray-800 rounded text-sm">
                              <div className="flex items-center gap-3">
                                <span className="font-medium">${invoice.amount_paid}</span>
                                <span className="text-gray-500 dark:text-gray-400">{invoice.number || invoice.id.slice(0, 12)}</span>
                                <span className="text-gray-400 dark:text-gray-500 dark:text-gray-400">{formatTimestamp(invoice.created)}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className={`px-2 py-0.5 rounded text-xs ${
                                  invoice.status === 'paid' ? 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300' :
                                  invoice.status === 'open' ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300' :
                                  'bg-gray-100 dark:bg-gray-700'
                                }`}>
                                  {invoice.status}
                                </span>
                                {invoice.hosted_invoice_url && (
                                  <a
                                    href={invoice.hosted_invoice_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:underline"
                                  >
                                    <ExternalLink className="h-3 w-3" />
                                  </a>
                                )}
                                {(invoice.status === 'open' || invoice.status === 'uncollectible') && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-6 px-2 text-xs"
                                    onClick={async () => {
                                      const result = await handleStripeAction('retry-invoice', selectedCustomer, { invoiceId: invoice.id });
                                      if (result) {
                                        await fetchStripeData(selectedCustomer);
                                        await fetchCustomerDetail(selectedCustomer);
                                      }
                                    }}
                                    disabled={actionLoading}
                                  >
                                    <Play className="h-3 w-3 mr-1" />
                                    Retry
                                  </Button>
                                )}
                                {invoice.status === 'paid' && invoice.payment_intent && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-6 px-2 text-xs"
                                    onClick={() => {
                                      setSelectedInvoice(invoice);
                                      setShowRefundDialog(true);
                                    }}
                                  >
                                    <RotateCcw className="h-3 w-3 mr-1" />
                                    Refund
                                  </Button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-gray-500 dark:text-gray-400 text-sm py-4 text-center">
                    No active Stripe subscription found
                  </div>
                )}
              </div>
            )}

            {/* Payment History */}
            <div className="pt-4 border-t">
              <h4 className="font-medium mb-2">Payment History</h4>
              {customerDetail.payments.length === 0 ? (
                <p className="text-gray-500 dark:text-gray-400 text-sm">No payments yet</p>
              ) : (
                <div className="space-y-2">
                  {customerDetail.payments.map((payment) => (
                    <div key={payment.id} className="flex justify-between items-center p-2 bg-gray-50 dark:bg-gray-800 rounded">
                      <div>
                        <p className="font-medium">${payment.amount}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {payment.payment_type} - {new Date(payment.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <span className={`px-2 py-1 rounded text-xs ${
                        payment.status === 'completed' ? 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300' :
                        payment.status === 'failed' ? 'bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-300' :
                        'bg-gray-100 dark:bg-gray-700'
                      }`}>
                        {payment.status}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <CustomerEditorDialog
          open={showEditor}
          onClose={() => setShowEditor(false)}
          customer={editingCustomer}
          onSave={() => {
            fetchCustomers();
            fetchCustomerDetail(selectedCustomer);
          }}
        />

        {/* Change Price Dialog */}
        <ChangePriceDialog
          open={showChangePriceDialog}
          onClose={() => setShowChangePriceDialog(false)}
          currentAmount={stripeData?.subscription?.price?.amount || customerDetail?.customer?.monthly_rate}
          loading={actionLoading}
          onSubmit={async (data) => {
            const success = await handleStripeAction('change-price', selectedCustomer, data);
            if (success) {
              setShowChangePriceDialog(false);
            }
          }}
        />

        {/* Refund Dialog */}
        <RefundDialog
          open={showRefundDialog}
          onClose={() => {
            setShowRefundDialog(false);
            setSelectedInvoice(null);
          }}
          invoice={selectedInvoice}
          loading={actionLoading}
          onSubmit={async (data) => {
            const success = await handleStripeAction('refund', selectedCustomer, data);
            if (success) {
              setShowRefundDialog(false);
              setSelectedInvoice(null);
            }
          }}
        />
      </div>
    );
  }

  // Customer List View
  return (
    <div className="space-y-4">
      {/* Header with search, filter, and add button */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="flex flex-col sm:flex-row gap-2 flex-1 w-full sm:w-auto">
          <div className="relative flex-1 sm:max-w-xs">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search customers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Status</option>
            <option value="none">New</option>
            <option value="active">Active</option>
            <option value="paused">Paused</option>
            <option value="cancelled">Cancelled</option>
            <option value="past_due">Past Due</option>
          </select>
        </div>
        <Button onClick={handleAddCustomer}>
          <UserPlus className="h-4 w-4 mr-2" />
          Add Customer
        </Button>
      </div>

      {/* Customer list */}
      <div className="space-y-2">
        {filteredCustomers.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            {customers.length === 0
              ? 'No customers yet. Click "Add Customer" to create one.'
              : 'No customers match your search criteria.'}
          </div>
        ) : (
          filteredCustomers.map((customer) => (
            <Card key={customer.id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => fetchCustomerDetail(customer.id)}>
              <CardContent className="py-3 px-4">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <span className="font-medium">{customer.name}</span>
                      {getStatusBadge(customer.subscription_status)}
                      <span className={`px-2 py-0.5 rounded text-xs ${
                        customer.account_type === 'individual'
                          ? 'bg-purple-100 dark:bg-purple-900/50 text-purple-800 dark:text-purple-300'
                          : 'bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300'
                      }`}>
                        {customer.account_type === 'individual' ? 'Individual' : 'Parent'}
                      </span>
                      {customer.quickstart_enabled && customer.quickstart_paid && (
                        <span className="px-2 py-0.5 bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300 rounded text-xs">
                          QuickStart
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400 flex flex-wrap items-center gap-x-4 gap-y-1 mt-1">
                      <span className="flex items-center">
                        <Mail className="h-3 w-3 mr-1" />
                        {customer.email}
                      </span>
                      {customer.phone && (
                        <span className="flex items-center">
                          <Phone className="h-3 w-3 mr-1" />
                          {customer.phone}
                        </span>
                      )}
                      <span className="flex items-center">
                        <DollarSign className="h-3 w-3 mr-1" />
                        {customer.monthly_rate}/mo
                      </span>
                      {customer.next_billing_date && (
                        <span className="flex items-center">
                          <Calendar className="h-3 w-3 mr-1" />
                          Next: {new Date(customer.next_billing_date).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <Button variant="outline" size="sm" onClick={(e) => handleEditCustomer(customer, e)}>
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="sm">
                      <Eye className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      <CustomerEditorDialog
        open={showEditor}
        onClose={() => setShowEditor(false)}
        customer={editingCustomer}
        onSave={fetchCustomers}
      />
    </div>
  );
};

// ============================================
// Main Customer Management Component
// ============================================
const CustomerManagement = ({ activeView = 'customers' }) => {
  return (
    <div className="space-y-4">
      <PageTitle
        title="Customer Management"
        description="Manage and authorize member accounts for portal access"
        icon={<CreditCard className="h-6 w-6" />}
      />

      {activeView === 'settings' ? <SubscriptionSettingsEditor /> : <CustomerList />}
    </div>
  );
};

export default CustomerManagement;
